// ─── Time Formatting ──────────────────────────────────────────────────────────

export function formatDuration(seconds: number | null): string {
  if (seconds === null || isNaN(seconds)) return '--:--'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  const s = Math.floor(seconds % 60)
  if (h > 0) {
    return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
  }
  return `${m}:${String(s).padStart(2, '0')}`
}

// ─── File Size Formatting ─────────────────────────────────────────────────────

export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

// ─── Date Formatting ──────────────────────────────────────────────────────────

export function formatDate(iso: string): string {
  const d = new Date(iso)
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

export function formatRelativeDate(iso: string): string {
  const now = Date.now()
  const then = new Date(iso).getTime()
  const diff = now - then
  const day = 86400000
  if (diff < day) return 'Today'
  if (diff < day * 2) return 'Yesterday'
  if (diff < day * 7) return `${Math.floor(diff / day)} days ago`
  return formatDate(iso)
}

// ─── Filename Cleanup ─────────────────────────────────────────────────────────

export function cleanFilename(name: string): string {
  return name.replace(/\.[^.]+$/, '').replace(/[_.-]+/g, ' ').trim()
}

// ─── Video MIME Type Detection ────────────────────────────────────────────────

const SUPPORTED_TYPES = new Set([
  'video/mp4',
  'video/quicktime',
  'video/x-m4v',
  'video/webm',
  'video/x-matroska',
])

const SUPPORTED_EXTENSIONS = new Set(['.mp4', '.mov', '.m4v', '.webm', '.mkv'])

export function isVideoFile(name: string, mimeType?: string): boolean {
  if (mimeType && SUPPORTED_TYPES.has(mimeType)) return true
  const ext = name.slice(name.lastIndexOf('.')).toLowerCase()
  return SUPPORTED_EXTENSIONS.has(ext)
}

// ─── Playback Compatibility ──────────────────────────────────────────────────

export const BROADCAST_OUTPUT_CHANNEL = 'cloudplayer-output'

export type CompatibilityStatus = 'likely' | 'warning' | 'unsupported'

export interface CompatibilityInput {
  name: string
  mimeType?: string | null
  durationSeconds?: number | null
}

export interface CompatibilityInfo {
  status: CompatibilityStatus
  label: string
  container: string
  mimeType: string
  notes: string[]
}

const PLAYABLE_VIDEO = new Set(['video/mp4', 'video/x-m4v'])
const WARNING_VIDEO = new Set(['video/quicktime', 'video/webm'])
const UNSUPPORTED_VIDEO = new Set(['video/x-matroska', 'video/avi', 'video/x-msvideo'])

function getExtension(name: string): string {
  const dot = name.lastIndexOf('.')
  return dot >= 0 ? name.slice(dot + 1).toLowerCase() : ''
}

function canPlayMime(mimeType: string): boolean {
  if (typeof document === 'undefined' || !mimeType) return false
  const video = document.createElement('video')
  return video.canPlayType(mimeType) !== ''
}

export function getCompatibilityInfo(
  item: CompatibilityInput,
  options: { bumper?: boolean } = {},
): CompatibilityInfo {
  const ext = getExtension(item.name)
  const mimeType = item.mimeType?.toLowerCase() ?? ''
  const notes: string[] = []
  let status: CompatibilityStatus = 'warning'
  let label = 'Check'
  let container = ext ? ext.toUpperCase() : 'Unknown'

  if (PLAYABLE_VIDEO.has(mimeType) || ext === 'mp4' || ext === 'm4v') {
    status = 'likely'
    label = 'Playable'
    container = ext === 'm4v' ? 'M4V' : 'MP4'
    notes.push('Best browser path: MP4 container with H.264 video and AAC audio.')
  } else if (WARNING_VIDEO.has(mimeType) || ext === 'mov' || ext === 'webm') {
    status = 'warning'
    label = 'Check'
    container = ext ? ext.toUpperCase() : mimeType === 'video/webm' ? 'WEBM' : 'MOV'
    notes.push('May depend on browser, codec, or how the file was exported.')
  } else if (UNSUPPORTED_VIDEO.has(mimeType) || ['mkv', 'avi', 'wmv'].includes(ext)) {
    status = 'unsupported'
    label = 'Risky'
    container = ext ? ext.toUpperCase() : 'Unsupported'
    notes.push('Likely to fail in the browser video element. Convert to MP4/H.264/AAC.')
  } else {
    notes.push('Unknown container or MIME type. Test before using this in a live room.')
  }

  if (mimeType && !canPlayMime(mimeType)) {
    if (status === 'likely') status = 'warning'
    notes.push('This browser does not advertise support for the reported MIME type.')
  }

  if (options.bumper) {
    if (status === 'likely') {
      label = 'Bumper OK'
    }
    if (item.durationSeconds === null || item.durationSeconds === undefined) {
      status = status === 'unsupported' ? 'unsupported' : 'warning'
      notes.push('Bumper duration is unknown. Keep live bumpers between 5 and 15 seconds.')
    } else if (item.durationSeconds > 15) {
      status = status === 'unsupported' ? 'unsupported' : 'warning'
      notes.push('Bumpers should be 15 seconds or shorter.')
    } else if (item.durationSeconds < 5) {
      notes.push('Very short bumper. Fine for stings, but check pacing in the room.')
    }

    if (container !== 'MP4' && container !== 'M4V') {
      status = status === 'unsupported' ? 'unsupported' : 'warning'
      notes.push('Bumpers should be MP4/H.264/AAC for the least surprising playback.')
    }
  }

  return {
    status,
    label,
    container,
    mimeType: mimeType || 'unknown',
    notes: [...new Set(notes)],
  }
}

// ─── Shuffle ──────────────────────────────────────────────────────────────────

export function shuffleArray<T>(arr: T[], seed?: number): T[] {
  const copy = [...arr]
  let s = seed ?? Date.now()

  // Seeded LCG pseudo-random
  const rand = () => {
    s = (s * 1664525 + 1013904223) & 0xffffffff
    return (s >>> 0) / 0xffffffff
  }

  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(rand() * (i + 1))
    ;[copy[i], copy[j]] = [copy[j], copy[i]]
  }
  return copy
}

// Weighted random ordering without replacement. A video with fewer completed
// plays gets a larger weight, so shuffle stays surprising without repeatedly
// favoring the same familiar items.
export function fairShuffle<T extends { playCount?: number }>(items: T[]): T[] {
  return items
    .map((item) => ({
      item,
      // Efraimidis-Spirakis weighted sampling key; higher weight means an
      // earlier expected position, and every item still appears exactly once.
      key: Math.pow(Math.random(), 1 + Math.max(0, item.playCount ?? 0)),
    }))
    .sort((a, b) => b.key - a.key)
    .map(({ item }) => item)
}

// ─── Debounce ─────────────────────────────────────────────────────────────────

export function debounce<T extends (...args: Parameters<T>) => void>(
  fn: T,
  ms: number,
): (...args: Parameters<T>) => void {
  let timer: ReturnType<typeof setTimeout>
  return (...args) => {
    clearTimeout(timer)
    timer = setTimeout(() => fn(...args), ms)
  }
}

// ─── Clamp ────────────────────────────────────────────────────────────────────

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

// ─── Color Helpers ───────────────────────────────────────────────────────────

export function hexToRgbTriplet(hex: string): string {
  const normalized = /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : '#3B8BEB'
  const value = normalized.slice(1)
  const r = parseInt(value.slice(0, 2), 16)
  const g = parseInt(value.slice(2, 4), 16)
  const b = parseInt(value.slice(4, 6), 16)
  return `${r} ${g} ${b}`
}

export function dimHexColor(hex: string): string {
  const normalized = /^#[0-9a-fA-F]{6}$/.test(hex) ? hex : '#3B8BEB'
  const value = normalized.slice(1)
  const r = Math.round(parseInt(value.slice(0, 2), 16) * 0.55)
  const g = Math.round(parseInt(value.slice(2, 4), 16) * 0.55)
  const b = Math.round(parseInt(value.slice(4, 6), 16) * 0.55)
  return `${r} ${g} ${b}`
}
